/**
 * What the incident panels and the anomaly list record, carried to the
 * platform. (The safety board's own reports and hazards are filed by React
 * dialogs — features/safety — not through here.)
 *
 *   incident         a REPORT AN INCIDENT entry, or an emergency stood down.
 *                    A security entry goes to the security register — the one
 *                    the executive security figure counts; anything else is
 *                    an open safety report marked `register: incident`, so the
 *                    incident panels read back their own entries and not every
 *                    report the safety office holds.
 *   incident.status  MARK AS INVESTIGATING and CLOSE THE INCIDENT.
 *   anomaly.ack      an acknowledged anomaly, as a site note any screen reads.
 *
 * Who filed it and when are the platform's to stamp. The name typed on the
 * form travels as `reported_as` — what the person said — never as the author.
 *
 * Nothing here imports the demo domain: this module is in the entry chunk.
 */
import { t } from "../../../i18n";
import { ANOMALY_ACK_TOPIC } from "../../../domain/safety";
import { registerWrite, type WriteContext } from "../writes";

/** The board's incident record, as far as a write reads it. */
interface BoardIncident {
  id: string;
  at?: string;
  title: string;
  cat?: string;
  secCat?: string;
  domain?: string;
  area?: string;
  sev?: number;
  by?: string;
  detail?: string;
  action?: string;
  photos?: string[];
  wo?: string | null;
  /** the work order raised on a first try, reused when the person tries again */
  woPlatform?: { id: string; number: string };
  woPhotosSent?: boolean;
}

interface IncidentExtra {
  wo?: boolean;
  security?: boolean;
  restricted?: boolean;
  /** an emergency stood down: filed, then closed at once with this reason */
  closeWith?: string;
}

interface StatusExtra {
  to: "investigating" | "closed";
  outcome?: string;
  id?: string | null;
  register?: string | null;
  reference?: string;
}

const DOMAIN_LABEL: Record<string, string> = {
  airside: "Airside",
  terminal: "Terminal",
  landside: "Landside",
  facility: "Facilities",
  security: "Security",
};

const INCIDENT_KEYS = (site: string) => [["security-incidents", site], ["safety-reports", site], ["kpis", site], ["work-orders"]];

/** The board's severity (1 serious … 3 minor) as the security register's (5 worst … 1). */
export const toPlatformSeverity = (sev: number | undefined): number => (sev === 1 ? 5 : sev === 3 ? 2 : 3);

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

async function photoFile(dataUrl: string, n: number): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], `incident-photo-${n}.jpg`, { type: blob.type || "image/jpeg" });
}

/** The follow-up work order, raised in the register itself. A failure is said, not thrown. */
async function followUp(ctx: WriteContext, rec: BoardIncident): Promise<string | null> {
  if (rec.woPlatform) return null;
  const where = rec.area || DOMAIN_LABEL[rec.domain ?? ""] || "";
  try {
    const wo = await ctx.source.createWorkOrder({
      siteId: ctx.siteId,
      title: where ? `Incident follow-up — ${where}` : "Incident follow-up",
      kind: "corrective",
      priority: rec.sev === 1 ? 1 : rec.sev === 3 ? 3 : 2,
      description: `${rec.title}. ${rec.detail ?? ""}`.trim().slice(0, 400),
    });
    rec.woPlatform = { id: wo.id, number: wo.number };
    return null;
  } catch (e) {
    return message(e);
  }
}

registerWrite("incident", {
  permission: (_rec, extra: IncidentExtra | null) =>
    extra?.security ? "security:incident_write" : "safety:report",
  invalidate: INCIDENT_KEYS,
  /* both registers honour the record's key, and a work order raised on a first
     try is kept on the record and reused — so TRY AGAIN files it once */
  retryable: true,
  async send(ctx, rec: BoardIncident, extra: IncidentExtra | null) {
    const woWhy = extra?.wo ? await followUp(ctx, rec) : null;
    const wo = rec.woPlatform;
    const detail = {
      domain: rec.domain,
      area: rec.area || undefined,
      action: rec.action || undefined,
      reported_as: rec.by || undefined,
      board_id: rec.id,
      ...(wo ? { work_order: wo } : {}),
    };

    let id: string;
    let reference: string;
    let kind: "security" | "safety";
    const notes: string[] = [];
    let warn = false;

    if (extra?.security) {
      if (!rec.secCat) {
        throw new Error(t("Choose the security category. The security register takes no entry without one."));
      }
      const raised = await ctx.source.raiseSecurityIncident(
        {
          siteId: ctx.siteId,
          category: rec.secCat,
          title: rec.title,
          severity: toPlatformSeverity(rec.sev),
          summary: rec.detail || undefined,
          occurredAt: rec.at,
          detail,
        },
        ctx.key,
      );
      ({ id, reference } = raised);
      kind = "security";
      if (wo) {
        try {
          await ctx.source.linkSecurityIncident(id, { subjectKind: "work_order", subjectId: wo.id, note: "follow-up work" });
        } catch {
          /* the work order names the incident in its description; the link is a convenience */
        }
      }
      if (raised.notification?.required) {
        warn = true;
        notes.push(
          t("Notify {to} within {hours} hours.", {
            to: raised.notification.to ?? t("the regulator"),
            hours: raised.notification.withinHours ?? "?",
          }),
        );
      }
    } else {
      const filed = await ctx.source.fileSafetyReport(
        {
          siteId: ctx.siteId,
          title: rec.title,
          /* the platform keeps no report without an account; the title is the least one */
          narrative: rec.detail?.trim() || rec.title,
          confidentiality: "open",
          kind: "occurrence",
          where: rec.area || undefined,
          category: rec.cat,
          occurredAt: rec.at,
          attrs: { register: "incident", severity: rec.sev, ...detail },
        },
        ctx.key,
      );
      ({ id, reference } = filed);
      kind = "safety";
    }

    if (wo) {
      rec.wo = wo.number;
      notes.push(t("Work order {wo} raised.", { wo: wo.number }));
      if (!rec.woPhotosSent && rec.photos?.length) {
        rec.woPhotosSent = true;
        try {
          for (const [n, p] of rec.photos.entries()) {
            await ctx.source.uploadWorkOrderPhoto(wo.id, await photoFile(p, n + 1), "before");
          }
        } catch (e) {
          warn = true;
          notes.push(t("The photographs did not reach the work order: {why}", { why: message(e) }));
        }
      }
    } else if (woWhy) {
      warn = true;
      notes.push(t("The work order was not raised: {why}", { why: woWhy }));
    }

    if (extra?.closeWith) {
      try {
        if (kind === "security") {
          await ctx.source.setSecurityIncidentStatus(id, { status: "closed", outcome: extra.closeWith });
        } else {
          await ctx.source.setSafetyReportStatus(id, { status: "closed", outcome: extra.closeWith });
        }
      } catch (e) {
        warn = true;
        notes.push(t("It is still open on the platform: {why}", { why: message(e) }));
      }
    }

    return {
      id,
      reference,
      kind,
      tone: warn ? "warn" : "ok",
      text: [t("{ref} saved on the platform.", { ref: reference }), ...notes].join(" "),
    };
  },
});

registerWrite("incident.status", {
  permission: (_rec, extra: StatusExtra | null) =>
    extra?.register === "security" ? "security:incident_write" : "safety:write",
  invalidate: INCIDENT_KEYS,
  /* a status sent twice is refused the second time, never applied twice */
  retryable: true,
  async send(ctx, rec: BoardIncident, extra: StatusExtra | null) {
    if (!extra?.id || !extra.register) {
      throw new Error(t("This incident is not on the platform yet, so its status stays on this page."));
    }
    const reference = extra.reference ?? rec.id;
    if (extra.register === "security") {
      const r = await ctx.source.setSecurityIncidentStatus(extra.id, { status: extra.to, outcome: extra.outcome });
      return {
        id: extra.id,
        reference,
        kind: "security",
        tone: r.warnings.length ? "warn" : "ok",
        text: [t("{ref} is now {status} on the platform.", { ref: reference, status: t(r.status) }), ...r.warnings].join(" "),
      };
    }
    const r = await ctx.source.setSafetyReportStatus(extra.id, {
      status: extra.to === "closed" ? "closed" : "investigating",
      outcome: extra.outcome,
    });
    return {
      id: extra.id,
      reference,
      kind: "safety",
      text: t("{ref} is now {status} on the platform.", { ref: reference, status: t(r.status) }),
    };
  },
});

registerWrite("anomaly.ack", {
  permission: () => "note:write",
  invalidate: (site) => [["notes", site]],
  /* a second note saying the same thing changes nothing anyone reads */
  retryable: true,
  async send(ctx, _rec, extra: { key?: string } | null) {
    if (!extra?.key) throw new Error(t("Nothing to acknowledge."));
    await ctx.source.addNote(ctx.siteId, {
      topic: ANOMALY_ACK_TOPIC,
      category: "observation",
      body: JSON.stringify({ key: extra.key, act: "ack" }),
    });
    return { id: "", reference: "", text: t("Acknowledgement saved on the platform.") };
  },
});
